import { orgAPI } from "@/api/api";
import type { IntegrationConnection } from "@cohesive/api-client";
import type { MicrosoftTeamsChannelEntity } from "@cohesive/api-entities";
import { useQueries, UseQueryOptions } from "@tanstack/react-query";
import { useMicrosoftTeamsTeams } from "./useMicrosoftTeamsTeams";

interface UseMicrosoftTeamsChannelsProps {
  connection: IntegrationConnection;
  options?: Partial<UseQueryOptions<MicrosoftTeamsChannelEntity[], Error>>;
}

export function isMicrosoftTeamsChannelEntity(
  entity: any,
): entity is MicrosoftTeamsChannelEntity {
  return entity.type === "microsoft_teams_channel";
}

export function useMicrosoftTeamsChannels({
  connection,
  options = {},
}: UseMicrosoftTeamsChannelsProps) {
  const { data: teamsData = [] } = useMicrosoftTeamsTeams({
    connection,
    options: {
      enabled: !!connection.integration_id,
    },
  });

  const results = useQueries({
    queries: teamsData.map((team) => ({
      queryKey: ["microsoft-teams-list-channels", connection.id, team.data.id],
      queryFn: async () => {
        const response = await orgAPI.toolbox.execute<
          MicrosoftTeamsChannelEntity[],
          { team_id: string }
        >({
          integration_connection_id: connection.id,
          action_name: "microsoft-teams-list-channels",
          action_input: { team_id: team.data.id },
        });
        return response.data;
      },
      enabled: !!connection.integration_id,
      ...options,
    })),
  });

  return {
    data: results
      .filter((result) => result.data)
      .map((result) => result.data)
      .flat()
      .filter(isMicrosoftTeamsChannelEntity),
    isLoading: results.some((result) => result.isLoading),
    error: results.find((result) => result.error),
  };
}
